import { createContext, useContext, useEffect, useState } from "react";
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useTranslation } from "../hooks/useTranslation";
import { isLikelyFrench, translatePreservingFrench } from "../utils/lang";

const ContentContext = createContext();

export const ContentProvider = ({ children }) => {
  // Original Firestore data (always in English)
  const [originalCategories, setOriginalCategories] = useState([]);
  const [originalSeries, setOriginalSeries] = useState([]);
  const [originalLiveEpisodes, setOriginalLiveEpisodes] = useState([]);

  // Translated data for display
  const [categories, setCategories] = useState([]);
  const [series, setSeries] = useState([]);
  const [liveEpisodes, setLiveEpisodes] = useState([]);

  const [loading, setLoading] = useState(true);
  const [translating, setTranslating] = useState(false);

  const { currentLanguage, translateText } = useTranslation();

  // Translation cache to avoid re-translating same content
  const [translationCache, setTranslationCache] = useState(new Map());
  const TRANSLATION_CACHE_VERSION = "10"; // bump to invalidate old entries

  const getCachedTranslation = async (text, targetLang) => {
    if (!text || targetLang === "en") return text;

    const normalizedTarget = (targetLang || "").toLowerCase();
    const cacheKey = `${TRANSLATION_CACHE_VERSION}|${normalizedTarget}|${text}`;
    if (translationCache.has(cacheKey)) {
      return translationCache.get(cacheKey);
    }

    try {
      const out =
        normalizedTarget === "fr"
          ? await translatePreservingFrench(text, "fr", translateText)
          : await translateText(text, normalizedTarget);

      setTranslationCache((prev) => new Map(prev.set(cacheKey, out)));
      return out;
    } catch (e) {
      console.error("Translation error:", e);
      return text;
    }
  };

  // Fetch data from Firestore (runs once)
  useEffect(() => {
    // Categories listener
    const unsubscribeCategories = onSnapshot(
      doc(db, "content", "main"),
      (docSnap) => {
        const categoriesData = docSnap.data()?.categories || [];
        setOriginalCategories(categoriesData);
      }
    );

    // Series collection listener
    const seriesRef = collection(db, "content", "main", "series");
    const unsubscribeSeries = onSnapshot(seriesRef, (seriesSnapshot) => {
      const seriesData = seriesSnapshot.docs.map((seriesDoc) => ({
        id: seriesDoc.id,
        ...seriesDoc.data(),
      }));

      setOriginalSeries(seriesData);
      setLoading(false);
    });

    // Live episodes listener
    const liveEpisodesQuery = query(
      collection(db, "episodes"),
      where("isLive", "==", true)
    );

    const unsubscribeLiveEpisodes = onSnapshot(
      liveEpisodesQuery,
      (snapshot) => {
        const liveEpisodesData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setOriginalLiveEpisodes(liveEpisodesData);
      }
    );

    return () => {
      unsubscribeCategories();
      unsubscribeSeries();
      unsubscribeLiveEpisodes();
    };
  }, []);

  // Translate categories when language changes
  const translateCategories = async (categoriesToTranslate) => {
    if (currentLanguage === "en") {
      return categoriesToTranslate;
    }

    return Promise.all(
      categoriesToTranslate.map(async (category) => {
        const sourceLangHint =
          category.language ||
          category.lang ||
          category.locale ||
          category.originalLanguage ||
          category.sourceLang;

        return {
          ...category,
          name: await getCachedTranslation(category.name, currentLanguage, {
            sourceLangHint,
          }),
          description: category.description
            ? await getCachedTranslation(
                category.description,
                currentLanguage,
                { sourceLangHint }
              )
            : category.description,
        };
      })
    );
  };

  // Translate series when language changes
  const translateSeries = async (seriesToTranslate) => {
    if (currentLanguage === "en") {
      return seriesToTranslate;
    }

    return Promise.all(
      seriesToTranslate.map(async (seriesItem) => {
        const sourceLangHint =
          seriesItem.language ||
          seriesItem.lang ||
          seriesItem.locale ||
          seriesItem.originalLanguage ||
          seriesItem.sourceLang;

        return {
          ...seriesItem,
          title: await getCachedTranslation(seriesItem.title, currentLanguage, {
            sourceLangHint,
          }),
          description: seriesItem.description
            ? await getCachedTranslation(
                seriesItem.description,
                currentLanguage,
                { sourceLangHint }
              )
            : seriesItem.description,
          // Translate episodes if they exist
          episodes: seriesItem.episodes
            ? await Promise.all(
                seriesItem.episodes.map(async (episode) => {
                  const epSourceLangHint =
                    episode.language ||
                    episode.lang ||
                    episode.locale ||
                    episode.originalLanguage ||
                    episode.sourceLang ||
                    sourceLangHint;

                  return {
                    ...episode,
                    title: await getCachedTranslation(
                      episode.title,
                      currentLanguage,
                      { sourceLangHint: epSourceLangHint }
                    ),
                    description: episode.description
                      ? await getCachedTranslation(
                          episode.description,
                          currentLanguage,
                          { sourceLangHint: epSourceLangHint }
                        )
                      : episode.description,
                  };
                })
              )
            : seriesItem.episodes,
        };
      })
    );
  };

  // Translate live episodes when language changes
  const translateLiveEpisodes = async (episodesToTranslate) => {
    if (currentLanguage === "en") {
      return episodesToTranslate;
    }

    return Promise.all(
      episodesToTranslate.map(async (episode) => {
        const sourceLangHint =
          episode.language ||
          episode.lang ||
          episode.locale ||
          episode.originalLanguage ||
          episode.sourceLang;

        return {
          ...episode,
          title: await getCachedTranslation(episode.title, currentLanguage, {
            sourceLangHint,
          }),
          description: episode.description
            ? await getCachedTranslation(episode.description, currentLanguage, {
                sourceLangHint,
              })
            : episode.description,
        };
      })
    );
  };

  // Handle translation when language changes or data updates
  useEffect(() => {
    const handleTranslation = async () => {
      if (loading) return; // Don't translate while still loading

      setTranslating(true);

      try {
        // Translate all data in parallel
        const [translatedCategories, translatedSeries, translatedLiveEpisodes] =
          await Promise.all([
            translateCategories(originalCategories),
            translateSeries(originalSeries),
            translateLiveEpisodes(originalLiveEpisodes),
          ]);

        setCategories(translatedCategories);
        setSeries(translatedSeries);
        setLiveEpisodes(translatedLiveEpisodes);
      } catch (error) {
        console.error("Translation error:", error);
        // Fallback to original data
        setCategories(originalCategories);
        setSeries(originalSeries);
        setLiveEpisodes(originalLiveEpisodes);
      } finally {
        setTranslating(false);
      }
    };

    handleTranslation();
  }, [
    currentLanguage,
    originalCategories,
    originalSeries,
    originalLiveEpisodes,
    loading,
  ]);

  const contextValue = {
    categories,
    series,
    liveEpisodes,
    loading: loading || translating,
    isTranslating: translating,
    currentLanguage,
    // Provide access to original data if needed
    originalData: {
      categories: originalCategories,
      series: originalSeries,
      liveEpisodes: originalLiveEpisodes,
    },
  };

  return (
    <ContentContext.Provider value={contextValue}>
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () => useContext(ContentContext);

// Optional: Helper hook for manual translation of specific content
export const useContentTranslation = () => {
  const { translateText, currentLanguage } = useTranslation();
  const [translationCache] = useState(new Map());

  const translateContent = async (
    content,
    fields = ["title", "description"]
  ) => {
    if (currentLanguage === "en" || !content) return content;

    const sourceLangHint =
      content.language ||
      content.lang ||
      content.locale ||
      content.originalLanguage ||
      content.sourceLang;

    const translated = { ...content };

    for (const field of fields) {
      if (translated[field] && typeof translated[field] === "string") {
        const value = translated[field];

        // Skip if we know (or strongly suspect) the value is already in the target language
        if (
          (sourceLangHint &&
            sourceLangHint.toLowerCase() === currentLanguage) ||
          (currentLanguage === "fr" && isLikelyFrench(value))
        ) {
          continue;
        }

        const cacheKey = `${value}-${currentLanguage}`;
        if (translationCache.has(cacheKey)) {
          translated[field] = translationCache.get(cacheKey);
        } else {
          try {
            const translation = await translateText(value, currentLanguage);
            translationCache.set(cacheKey, translation);
            translated[field] = translation;
          } catch (error) {
            console.error(`Translation error for ${field}:`, error);
          }
        }
      }
    }

    return translated;
  };

  return { translateContent };
};
